import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { UsuarioVista } from './tipos.ts';
import { EVENTO_SESION_EXPIRADA, apiFetch, leerToken, login as apiLogin, logout as apiLogout } from './api.ts';

interface AuthState {
  usuario: UsuarioVista | null;
  cargando: boolean;
  login: (email: string, password: string) => Promise<UsuarioVista>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioVista | null>(null);
  const [cargando, setCargando] = useState<boolean>(true);

  useEffect(() => {
    if (!leerToken()) {
      setCargando(false);
      return;
    }
    let cancelado = false;
    (async () => {
      try {
        const r = await apiFetch<{ usuario: UsuarioVista }>('/api/auth/me');
        if (!cancelado) setUsuario(r.usuario);
      } catch {
        if (!cancelado) setUsuario(null);
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  // Cuando el refresh falla de verdad (revocado/vencido) el estado tiene que
  // reflejarlo sin recargar: App redirige a /login al quedar sin usuario.
  useEffect(() => {
    const alExpirar = () => setUsuario(null);
    window.addEventListener(EVENTO_SESION_EXPIRADA, alExpirar);
    return () => window.removeEventListener(EVENTO_SESION_EXPIRADA, alExpirar);
  }, []);

  const entrar = useCallback(async (email: string, password: string) => {
    const u = await apiLogin(email, password);
    setUsuario(u);
    return u;
  }, []);

  const salir = useCallback(async () => {
    setUsuario(null);
    await apiLogout();
  }, []);

  const value = useMemo<AuthState>(
    () => ({ usuario, cargando, login: entrar, logout: salir }),
    [usuario, cargando, entrar, salir],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}